import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Star, TrendingUp } from 'lucide-react';
import { productsApi } from '../api/cms';
import { defaultProducts } from '../api/defaults';
import type { Product } from '../types';
import { RichEditor, MediaPickerModal } from '../components/RichEditor';

const TYPES = ['hosting', 'webdesign', 'callcenter', 'voip', 'crm', 'hardware', 'other'] as const;
const PERIODS = [{ v: 'once', l: 'O singură dată' }, { v: 'monthly', l: 'Lunar' }, { v: 'yearly', l: 'Anual' }];
const CURRENCIES = ['RON', 'EUR'] as const;

const emptyProduct: Omit<Product, 'id' | 'updatedAt'> = {
  name: '', slug: '', type: 'hosting', price: 0, currency: 'EUR', period: 'monthly',
  description: '', features: [], highlighted: false, status: 'active',
  upsellIds: [], crosssellIds: [], image: '',
  seo: { title: '', description: '', keywords: '' },
  requiresShipping: false, dimensions: { l: 0, w: 0, h: 0 }, weight: 0,
  discount12m: 0, discount24m: 5, discount36m: 10, purchasePrice: 0, supplier: '',
};

function slugify(t: string) {
  return t.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export default function ProductEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const [form, setForm] = useState<Omit<Product, 'id' | 'updatedAt'>>({ ...emptyProduct, seo: { ...emptyProduct.seo } });
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<'main' | 'relations' | 'seo' | 'gallery'>('main');
  const [gallery, setGallery] = useState<string[]>([]);
  const [featuresText, setFeaturesText] = useState('');
  const [showMedia, setShowMedia] = useState(false);
  const [mediaTarget, setMediaTarget] = useState<'cover' | 'insert'>('cover');
  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => {
    productsApi.list().then(setAllProducts).catch(() => setAllProducts(defaultProducts));
    if (!isNew) {
      productsApi.get(id!).then(p => {
        setForm({ name: p.name, slug: p.slug, type: p.type, price: p.price, currency: p.currency, period: p.period, description: p.description, features: p.features, highlighted: p.highlighted, status: p.status, upsellIds: p.upsellIds, crosssellIds: p.crosssellIds, image: p.image, seo: { ...p.seo }, requiresShipping: p.requiresShipping ?? false, dimensions: p.dimensions ?? { l: 0, w: 0, h: 0 }, weight: p.weight ?? 0, discount12m: p.discount12m ?? 0, discount24m: p.discount24m ?? 5, discount36m: p.discount36m ?? 10, purchasePrice: p.purchasePrice ?? 0, supplier: (p as unknown as Record<string,string>)['supplier'] ?? '' });
        setFeaturesText(p.features.join('\n'));
      }).catch(() => {
        const p = defaultProducts.find(x => x.id === id);
        if (p) {
          setForm({ name: p.name, slug: p.slug, type: p.type, price: p.price, currency: p.currency, period: p.period, description: p.description, features: p.features, highlighted: p.highlighted, status: p.status, upsellIds: p.upsellIds, crosssellIds: p.crosssellIds, image: p.image, seo: { ...p.seo }, requiresShipping: p.requiresShipping ?? false, dimensions: p.dimensions ?? { l: 0, w: 0, h: 0 }, weight: p.weight ?? 0, discount12m: p.discount12m ?? 0, discount24m: p.discount24m ?? 5, discount36m: p.discount36m ?? 10, purchasePrice: p.purchasePrice ?? 0, supplier: '' });
          setFeaturesText(p.features.join('\n'));
        }
      });
    }
  }, [id, isNew]);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm(f => ({ ...f, [k]: v }));
  const setSeo = (k: string, v: string) => setForm(f => ({ ...f, seo: { ...f.seo, [k]: v } }));

  const handleFeaturesChange = (text: string) => {
    setFeaturesText(text);
    set('features', text.split('\n').filter(Boolean));
  };
  const handleNameChange = (v: string) => { set('name', v); if (isNew) set('slug', slugify(v)); };

  const toggleRelation = (arr: 'upsellIds' | 'crosssellIds', pid: string) => {
    setForm(f => { const cur = f[arr]; return { ...f, [arr]: cur.includes(pid) ? cur.filter((x: string) => x !== pid) : [...cur, pid] }; });
  };

  const handleMediaSelect = (url: string) => {
    if (mediaTarget === 'cover') {
      set('image', url);
    } else {
      const imgHtml = `<img src="${url}" alt="" style="max-width:100%;height:auto;border-radius:8px;margin:10px 0;" />`;
      setForm(f => ({ ...f, description: f.description + imgHtml }));
      setEditorKey(k => k + 1);
    }
    setShowMedia(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await productsApi.create(form);
      else await productsApi.update(id!, form);
      navigate('/products');
    } catch { setSaving(false); }
  };

  const Input = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
    <input {...props} className="w-full bg-[#0d1117] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors" />
  );
  const Textarea = (props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
    <textarea {...props} className="w-full bg-[#0d1117] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors resize-none" />
  );
  const Select = (props: React.SelectHTMLAttributes<HTMLSelectElement> & { children: React.ReactNode }) => (
    <select {...props} className="w-full bg-[#0d1117] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-colors [&>option]:bg-[#0d1117] [&>option]:text-white">{props.children}</select>
  );
  const Label = ({ children }: { children: React.ReactNode }) => (
    <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">{children}</label>
  );

  const otherProducts = allProducts.filter(p => p.id !== id);

  // Margin calc
  const purchase = form.purchasePrice ?? 0;
  const sale = form.price ?? 0;
  const margin = purchase > 0 && sale > 0 ? ((sale - purchase) / purchase * 100) : null;
  const marginColor = margin === null ? '#666' : margin >= 30 ? '#4ade80' : margin >= 10 ? '#facc15' : '#f87171';

  return (
    <div className="space-y-5 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/products')} className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-all"><ArrowLeft size={18} /></button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-white">{isNew ? 'Produs nou' : 'Editare produs'}</h1>
        </div>
        <button onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
          <Save size={15} /> {saving ? 'Se salvează...' : 'Salvează'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10">
        {([['main', '📋 Detalii'], ['relations', '🔗 Upsell / Cross-sell'], ['seo', '🔍 SEO'], ['gallery', '🖼️ Galerie']] as const).map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t ? 'text-[#4ab8ff] border-[#295CA8]' : 'text-white/40 border-transparent hover:text-white'}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'main' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left — main fields */}
          <div className="lg:col-span-2 space-y-5">
            <div><Label>Nume produs</Label><Input value={form.name} onChange={e => handleNameChange(e.target.value)} placeholder="Ex: Hosting START" /></div>
            <div><Label>Slug URL</Label><Input value={form.slug} onChange={e => set('slug', e.target.value)} placeholder="hosting-start" /></div>

            {/* Description rich editor */}
            <div>
              <Label>Descriere produs (text rich / HTML)</Label>
              <RichEditor
                key={editorKey}
                value={form.description}
                onChange={v => set('description', v)}
                onInsertImageFromLibrary={() => { setMediaTarget('insert'); setShowMedia(true); }}
                minHeight={200}
                placeholder="Descriere detaliată a produsului..."
              />
            </div>

            <div>
              <Label>Caracteristici (câte una pe linie)</Label>
              <Textarea value={featuresText} onChange={e => handleFeaturesChange(e.target.value)} rows={7}
                placeholder={"10 GB SSD NVMe\n5 Conturi Email\nSSL Gratuit"} />
            </div>

            {/* Image with library picker */}
            <div>
              <Label>Imagine produs</Label>
              {form.image ? (
                <div className="relative rounded-xl overflow-hidden border border-white/10 group mb-2">
                  <img src={form.image} alt="Produs" className="w-full h-40 object-contain bg-white/3" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button type="button" onClick={() => { setMediaTarget('cover'); setShowMedia(true); }}
                      className="bg-[#295CA8] text-white text-xs px-3 py-1.5 rounded-lg">🔄 Schimbă</button>
                    <button type="button" onClick={() => set('image', '')}
                      className="bg-red-600 text-white text-xs px-3 py-1.5 rounded-lg">🗑 Șterge</button>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => { setMediaTarget('cover'); setShowMedia(true); }}
                  className="w-full h-32 border-2 border-dashed border-white/15 rounded-xl flex flex-col items-center justify-center text-white/30 hover:border-[#295CA8]/60 hover:text-white/60 transition-all mb-2">
                  <span className="text-2xl mb-1">🖼</span>
                  <span className="text-xs">Alege din Librărie Media</span>
                </button>
              )}
              <Input value={form.image} onChange={e => set('image', e.target.value)} placeholder="sau URL direct imagine..." />
            </div>
          </div>

          {/* Right sidebar */}
          <div className="space-y-4">
            <div><Label>Tip produs</Label>
              <Select value={form.type} onChange={e => set('type', e.target.value as typeof form.type)}>
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Preț vânzare</Label><Input type="number" value={form.price} onChange={e => set('price', Number(e.target.value))} /></div>
              <div><Label>Monedă</Label>
                <Select value={form.currency} onChange={e => set('currency', e.target.value as typeof form.currency)}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </Select>
              </div>
            </div>

            {/* Prețuri & Marjă */}
            <div className="rounded-xl border border-[#295CA8]/30 bg-[#295CA8]/5 p-4 space-y-3">
              <div className="flex items-center gap-2"><TrendingUp size={14} className="text-[#4ab8ff]" /><span className="text-xs font-semibold text-[#4ab8ff] uppercase tracking-wide">Prețuri & Marjă</span></div>
              <div><Label>Preț achiziție ({form.currency})</Label>
                <Input type="number" min={0} step={0.01} value={form.purchasePrice ?? 0} onChange={e => set('purchasePrice', parseFloat(e.target.value) || 0)} placeholder="0.00" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-white/5 rounded-lg p-2 text-center">
                  <div className="text-[9px] text-white/40 uppercase mb-0.5">Achiz.</div>
                  <div className="text-xs font-bold text-white">{purchase > 0 ? purchase.toFixed(2) : '—'}</div>
                </div>
                <div className="bg-white/5 rounded-lg p-2 text-center">
                  <div className="text-[9px] text-white/40 uppercase mb-0.5">Vânz.</div>
                  <div className="text-xs font-bold text-white">{sale > 0 ? sale.toFixed(2) : '—'}</div>
                </div>
                <div className="rounded-lg p-2 text-center" style={{ background: margin !== null ? 'rgba(74,184,255,0.08)' : 'rgba(255,255,255,0.03)' }}>
                  <div className="text-[9px] text-white/40 uppercase mb-0.5">Marjă</div>
                  <div className="text-xs font-bold" style={{ color: marginColor }}>{margin !== null ? `${margin.toFixed(1)}%` : '—'}</div>
                </div>
              </div>
            </div>

            <div><Label>Furnizor</Label><Input placeholder="HP Inc., eMAG Business..." value={form.supplier ?? ''} onChange={e => set('supplier', e.target.value)} /></div>

            <div><Label>Perioadă</Label>
              <Select value={form.period} onChange={e => set('period', e.target.value as typeof form.period)}>
                {PERIODS.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onChange={e => set('status', e.target.value as typeof form.status)}>
                <option value="active">Activ</option>
                <option value="inactive">Inactiv</option>
              </Select>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg border border-white/10 cursor-pointer" onClick={() => set('highlighted', !form.highlighted)}>
              <Star size={15} className={form.highlighted ? 'text-yellow-400' : 'text-white/30'} />
              <span className="text-sm text-white">Produs recomandat</span>
              <div className={`ml-auto w-10 h-5 rounded-full transition-colors ${form.highlighted ? 'bg-[#295CA8]' : 'bg-white/10'}`}>
                <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform mt-0.5 ${form.highlighted ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
            </div>

            {form.type === 'hosting' && (
              <div className="border border-[#295CA8]/30 rounded-lg p-4 space-y-3 bg-[#295CA8]/5">
                <Label>⏱ Discount-uri hosting</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[{k:'discount12m' as const,l:'12 luni'},{k:'discount24m' as const,l:'24 luni'},{k:'discount36m' as const,l:'36 luni'}].map(d=>(
                    <div key={d.k}><Label>{d.l} (%)</Label><Input type="number" min={0} max={50} value={form[d.k]??0} onChange={e=>set(d.k, Math.min(50,Math.max(0,parseFloat(e.target.value)||0)))} /></div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg border border-white/10 cursor-pointer" onClick={() => set('requiresShipping', !form.requiresShipping)}>
              <span>📦</span>
              <div className="flex-1"><span className="text-sm text-white">Necesită livrare curier</span></div>
              <div className={`w-10 h-5 rounded-full transition-colors ${form.requiresShipping ? 'bg-[#295CA8]' : 'bg-white/10'}`}>
                <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform mt-0.5 ${form.requiresShipping ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
            </div>
            {form.requiresShipping && (
              <div className="border border-white/10 rounded-lg p-4 space-y-3">
                <Label>Dimensiuni (cm) & Greutate (kg)</Label>
                <div className="grid grid-cols-3 gap-2">
                  <div><Label>L</Label><Input type="number" min={0} value={form.dimensions?.l??0} onChange={e=>set('dimensions',{...form.dimensions,l:parseFloat(e.target.value)||0})} /></div>
                  <div><Label>l</Label><Input type="number" min={0} value={form.dimensions?.w??0} onChange={e=>set('dimensions',{...form.dimensions,w:parseFloat(e.target.value)||0})} /></div>
                  <div><Label>H</Label><Input type="number" min={0} value={form.dimensions?.h??0} onChange={e=>set('dimensions',{...form.dimensions,h:parseFloat(e.target.value)||0})} /></div>
                </div>
                <div><Label>Greutate (kg)</Label><Input type="number" min={0} step={0.01} value={form.weight??0} onChange={e=>set('weight',parseFloat(e.target.value)||0)} /></div>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'relations' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {(['upsellIds', 'crosssellIds'] as const).map(rel => (
            <div key={rel}>
              <h3 className="text-sm font-semibold text-white mb-3">{rel === 'upsellIds' ? '⬆️ Upsell' : '↔️ Cross-sell'}</h3>
              <p className="text-xs text-white/30 mb-4">{rel === 'upsellIds' ? 'Produse premium sugerate ca upgrade.' : 'Produse complementare afișate alături.'}</p>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {otherProducts.map(p => (
                  <label key={p.id} className="flex items-center gap-3 p-3 bg-[#07090f] border border-white/10 rounded-lg cursor-pointer hover:border-[#295CA8]/40 transition-all">
                    <input type="checkbox" checked={form[rel].includes(p.id)} onChange={() => toggleRelation(rel, p.id)} className="accent-[#295CA8]" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-medium truncate">{p.name}</div>
                      <div className="text-xs text-white/30">{p.type} · {p.price} {p.currency}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'seo' && (
        <div className="space-y-5 max-w-2xl">
          <div className="bg-[#4ab8ff]/5 border border-[#4ab8ff]/15 rounded-xl p-4">
            <div className="text-xs text-[#4ab8ff] font-semibold mb-1">Preview Google</div>
            <div className="text-sm text-blue-400 underline truncate">{form.slug ? `https://interpersonal.ro/catalog/${form.slug}` : 'https://interpersonal.ro/catalog/...'}</div>
            <div className="text-sm text-white font-medium mt-1 truncate">{form.seo.title || form.name || 'Titlu produs'}</div>
            <div className="text-xs text-white/40 mt-0.5 line-clamp-2">{form.seo.description || 'Descriere produs pentru motoarele de căutare...'}</div>
          </div>
          <div><Label>Meta Title <span className="text-white/30 font-normal">(recomandat: max 60 caractere)</span></Label><Input value={form.seo.title} onChange={e => setSeo('title', e.target.value)} maxLength={70} placeholder={`Cumpara ${form.name} | Interpersonal IT`}/><div className="flex justify-between mt-1"><span className="text-xs text-white/30">Format recomandat: Produs + Beneficiu | Brand</span><span className={`text-xs ${form.seo.title.length > 60 ? 'text-yellow-400' : 'text-white/30'}`}>{form.seo.title.length}/60</span></div></div>
          <div><Label>Meta Description <span className="text-white/30 font-normal">(recomandat: 120–160 caractere)</span></Label><Textarea value={form.seo.description} onChange={e => setSeo('description', e.target.value)} rows={3} maxLength={200} placeholder="Descriere concisă și convingătoare a produsului pentru rezultatele din Google..."/><div className="flex justify-between mt-1"><span className="text-xs text-white/30">Includeți cuvinte cheie principale și un call-to-action</span><span className={`text-xs ${form.seo.description.length > 160 ? 'text-yellow-400' : 'text-white/30'}`}>{form.seo.description.length}/160</span></div></div>
          <div><Label>Keywords <span className="text-white/30 font-normal">(separate prin virgulă)</span></Label><Textarea value={form.seo.keywords} onChange={e => setSeo('keywords', e.target.value)} rows={2} placeholder="produs, categorie, brand, caracteristici cheie..."/></div>
          <div><Label>URL Slug</Label><div className="flex items-center gap-2"><span className="text-xs text-white/30 flex-shrink-0">interpersonal.ro/catalog/</span><Input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'') }))}/></div></div>
        </div>
      )}

      {tab === 'gallery' && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-white">Galerie Imagini</h3>
              <p className="text-xs text-white/40 mt-0.5">Imagini suplimentare pentru pagina produsului. Prima imagine este cover-ul principal.</p>
            </div>
            <button onClick={() => { setMediaTarget('cover'); setShowMedia(true); }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white transition-all"
              style={{ background: 'linear-gradient(135deg,#295CA8,#3D72B8)' }}>
              + Adaugă Imagine
            </button>
          </div>

          {/* Cover principal */}
          <div>
            <Label>Imagine Cover (principală)</Label>
            <div className="flex items-start gap-4">
              {form.image ? (
                <div className="relative group">
                  <img src={form.image} alt="cover" className="w-32 h-32 object-cover rounded-xl border border-white/10"/>
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                    <button onClick={() => { setMediaTarget('cover'); setShowMedia(true); }} className="px-2 py-1 text-xs bg-white/20 rounded-lg text-white hover:bg-white/30">Schimbă</button>
                    <button onClick={() => set('image', '')} className="px-2 py-1 text-xs bg-red-500/30 rounded-lg text-white hover:bg-red-500/50">Șterge</button>
                  </div>
                  <div className="absolute -top-1.5 -left-1.5 bg-[#295CA8] text-white text-[9px] px-1.5 py-0.5 rounded font-bold">COVER</div>
                </div>
              ) : (
                <button onClick={() => { setMediaTarget('cover'); setShowMedia(true); }}
                  className="w-32 h-32 rounded-xl border-2 border-dashed border-white/10 hover:border-[#295CA8] flex flex-col items-center justify-center gap-2 text-white/30 hover:text-[#4ab8ff] transition-all">
                  <span className="text-2xl">+</span>
                  <span className="text-xs">Cover</span>
                </button>
              )}
              <div className="flex-1 text-xs text-white/30 pt-2 space-y-1">
                <p>• Dimensiune recomandată: <strong className="text-white/50">800×600px</strong> sau <strong className="text-white/50">1:1</strong></p>
                <p>• Format acceptat: <strong className="text-white/50">WebP, JPG, PNG</strong></p>
                <p>• Mărime maximă: <strong className="text-white/50">500KB</strong></p>
              </div>
            </div>
          </div>

          {/* Galerie aditionala */}
          <div>
            <Label>Imagini Galerie ({gallery.length})</Label>
            <div className="grid grid-cols-4 gap-3">
              {gallery.map((url, i) => (
                <div key={i} className="relative group">
                  <img src={url} alt={`gallery-${i}`} className="w-full aspect-square object-cover rounded-xl border border-white/10"/>
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2 flex-col">
                    <div className="text-[10px] text-white/60">#{i+1}</div>
                    <button onClick={() => setGallery(g => g.filter((_, j) => j !== i))} className="px-2 py-1 text-xs bg-red-500/40 rounded-lg text-white">Șterge</button>
                  </div>
                </div>
              ))}
              <button onClick={() => {
                  setMediaTarget('cover');
                  setShowMedia(true);
                  (window as unknown as Record<string, unknown>)['__galleryMode'] = true;
                }}
                className="aspect-square rounded-xl border-2 border-dashed border-white/10 hover:border-[#295CA8] flex flex-col items-center justify-center gap-1 text-white/30 hover:text-[#4ab8ff] transition-all">
                <span className="text-xl">+</span>
                <span className="text-xs">Adaugă</span>
              </button>
            </div>
            <p className="text-xs text-white/25 mt-2">Imaginile galeriei sunt afișate pe pagina produsului în secțiunea de preview. Selectați imagini din Librăria Media.</p>
          </div>
        </div>
      )}

      {showMedia && <MediaPickerModal onSelect={(url) => {
          const isGalleryMode = (window as unknown as Record<string, unknown>)['__galleryMode'];
          if (isGalleryMode) {
            setGallery(g => [...g, url]);
            delete (window as unknown as Record<string, unknown>)['__galleryMode'];
          } else {
            handleMediaSelect(url);
          }
          setShowMedia(false);
        }} onClose={() => { setShowMedia(false); delete (window as unknown as Record<string, unknown>)['__galleryMode']; }} />}
    </div>
  );
}
